import { MatDialog } from '@angular/material/dialog';
import { DialogLayoutType, DialogPresets } from '@principle-theorem/ng-shared';
import { ScheduleSummary } from '@principle-theorem/principle-core';
import { type IAppointment } from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import {
  TakeDepositDialogComponent,
  type ITakeDepositDialogData,
} from './take-deposit-dialog/take-deposit-dialog.component';
import { TakeDepositEntry } from './take-deposit-entry';

/**
 * What every surface says when capture could not be reached, so an operator
 * hears the same thing whichever one they started from.
 */
export const COULD_NOT_OPEN_DEPOSIT_MESSAGE =
  'Could not open Take Deposit for this appointment';

/**
 * Why a {@link TakeDepositDialog.open} call did or did not reach capture.
 *
 * The two failure cases used to be bare `return`s. That hid a real defect: the
 * full-page create screen can save an appointment with no event, so it promised
 * "Save Appointment & Take Deposit" and then silently did nothing. Callers must
 * be able to tell "capture is under way" from "capture never started".
 */
export enum TakeDepositOpenResult {
  Opened = 'opened',
  /**
   * The appointment has no event, so it has no scheduled position to capture
   * against. An unscheduled appointment is not yet a commitment and its deposit
   * is not yet owing, so this is an expected outcome rather than an error.
   */
  MissingEvent = 'missingEvent',
  /** The appointment has an event but could not be summarised. */
  MissingSummary = 'missingSummary',
}

/**
 * The one way any surface hands an appointment to deposit capture.
 *
 * It serves two uses, which {@link TakeDepositEntry} distinguishes. While
 * scheduling an appointment it is a gate: reception decides then and there
 * whether to collect, so it is sealed and the decision has to be made rather
 * than dismissed. From the timeline sidebar it is instead a way to take or
 * chase a deposit that has not been requested yet, where deferring was already
 * decided and enforced at scheduling, so backing out leaves nothing worse than
 * it found.
 */
export class TakeDepositDialog {
  /**
   * Opens a persisted appointment in the sealed Take Deposit dialog.
   *
   * Returns why it did not open rather than failing silently, so a caller that
   * has already told the operator a deposit is about to be taken can correct
   * itself. See {@link TakeDepositOpenResult}.
   *
   * `beforeOpenFn` runs only once the summary has resolved and the dialog is
   * certain to open, so a caller that tears down its own surface first never
   * dismantles it for an open that turns out not to happen.
   */
  static async open(
    dialog: MatDialog,
    appointment: WithRef<IAppointment>,
    entry: TakeDepositEntry,
    beforeOpenFn?: () => void
  ): Promise<TakeDepositOpenResult> {
    const event = appointment.event;
    if (!event) {
      return TakeDepositOpenResult.MissingEvent;
    }
    const summary = await ScheduleSummary.getSummaryFromEventable({
      ...appointment,
      event,
    });
    if (!summary) {
      return TakeDepositOpenResult.MissingSummary;
    }
    beforeOpenFn?.();
    dialog.open<TakeDepositDialogComponent, ITakeDepositDialogData>(
      TakeDepositDialogComponent,
      DialogPresets.medium<ITakeDepositDialogData>(
        {
          data: { appointment: summary, entry },
          disableClose: entry === TakeDepositEntry.Booking,
        },
        DialogLayoutType.Principle
      )
    );
    return TakeDepositOpenResult.Opened;
  }
}
